import React from 'react';
import { Paper, Typography, Box, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { Dive } from '../types/Dive';
import { UnitPreferences, GasConsumptionMetric, formatDepth, formatTemperature, calculateDiveGasConsumption, formatGasConsumptionDisplayValue, getGasConsumptionMetricLabel } from '../utils/unitConversions';
import { ENABLE_GAS_ANALYTICS } from '../config/featureFlags';

interface DiveStatisticsProps {
  dives: Dive[];
  unitPreferences: UnitPreferences;
  gasConsumptionMetric: GasConsumptionMetric;
}

const DiveStatistics: React.FC<DiveStatisticsProps> = ({ dives, unitPreferences, gasConsumptionMetric }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Filter out dives with no meaningful data (e.g., depth of 0, invalid temperature) for averages
  const validDives = dives.filter(dive => 
    dive.maxDepth > 0 && 
    dive.avgDepth > 0 && 
    dive.waterTemperature > -10 // Exclude unrealistic temperatures
  );
  
  const totalDives = dives.length; // Show total count of all dives
  const totalDiveTime = dives.reduce((sum, dive) => {
    const match = dive.duration.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes, seconds] = match;
      return sum + (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseInt(seconds);
    }
    return sum;
  }, 0);
  
  const maxDepthRecord = Math.max(...dives.map(dive => dive.maxDepth), 0);
  // Calculate averages using only valid dives
  const avgDepth = validDives.length > 0 ? validDives.reduce((sum, dive) => sum + dive.avgDepth, 0) / validDives.length : 0;
  const avgTemp = validDives.length > 0 ? validDives.reduce((sum, dive) => sum + dive.waterTemperature, 0) / validDives.length : 0;

  const divesWithGasConsumption = dives
    .map((dive) => calculateDiveGasConsumption(dive, gasConsumptionMetric, unitPreferences.pressure))
    .filter((rate): rate is number => rate !== null);
  const avgGasConsumption = divesWithGasConsumption.length > 0
    ? divesWithGasConsumption.reduce((sum, rate) => sum + rate, 0) / divesWithGasConsumption.length
    : null;

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Trend: compare last 5 dives avg to the overall avg (threshold: >5% difference)
  const RECENT_COUNT = 5;
  const TREND_THRESHOLD = 0.05;
  const getTrend = (recentAvg: number, overallAvg: number, higherIsBetter: boolean): { trend: 'up' | 'down' | 'flat'; label: string } | undefined => {
    if (overallAvg === 0 || dives.length <= RECENT_COUNT) return undefined;
    const delta = (recentAvg - overallAvg) / Math.abs(overallAvg);
    if (Math.abs(delta) < TREND_THRESHOLD) return { trend: 'flat', label: 'No change vs. overall average' };
    if (delta > 0) return { trend: higherIsBetter ? 'up' : 'down', label: `${(delta * 100).toFixed(0)}% above overall average (last ${RECENT_COUNT} dives)` };
    return { trend: higherIsBetter ? 'down' : 'up', label: `${(Math.abs(delta) * 100).toFixed(0)}% below overall average (last ${RECENT_COUNT} dives)` };
  };

  const recentDives = dives.slice(-RECENT_COUNT);
  const recentValid = recentDives.filter(d => d.maxDepth > 0 && d.avgDepth > 0 && d.waterTemperature > -10);

  const recentAvgDepth = recentValid.length > 0 ? recentValid.reduce((s, d) => s + d.avgDepth, 0) / recentValid.length : 0;
  const recentAvgTemp = recentValid.length > 0 ? recentValid.reduce((s, d) => s + d.waterTemperature, 0) / recentValid.length : 0;
  const recentGasRates = recentDives.map(d => calculateDiveGasConsumption(d, gasConsumptionMetric, unitPreferences.pressure)).filter((r): r is number => r !== null);
  const recentAvgGas = recentGasRates.length > 0 ? recentGasRates.reduce((s, r) => s + r, 0) / recentGasRates.length : 0;

  const depthTrend = getTrend(recentAvgDepth, avgDepth, false); // deeper = more challenging, neutral
  const tempTrend = getTrend(recentAvgTemp, avgTemp, true);
  const gasTrend = avgGasConsumption !== null && recentAvgGas > 0 ? getTrend(recentAvgGas, avgGasConsumption, false) : undefined;

  const StatCard: React.FC<{ title: string; value: string; subtitle?: string; trend?: 'up' | 'down' | 'flat'; trendLabel?: string }> = ({ title, value, subtitle, trend, trendLabel }) => {
    const trendIcon = trend === 'up'
      ? <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', ml: 0.5 }} />
      : trend === 'down'
      ? <TrendingDownIcon fontSize="small" sx={{ color: 'error.main', ml: 0.5 }} />
      : trend === 'flat'
      ? <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
      : null;

    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          textAlign: 'center',
          ...(isMobile && { minWidth: 120, flex: '0 0 auto' }),
        }}
      >
        <Typography variant="h6" color="primary" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          {trendIcon && trendLabel ? (
            <Tooltip title={trendLabel} placement="top">
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>{trendIcon}</Box>
            </Tooltip>
          ) : trendIcon}
        </Box>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </Paper>
    );
  };

  const cards = (
    <>
      <StatCard title="Total Dives" value={totalDives.toString()} />
      <StatCard title="Total Time" value={formatTime(totalDiveTime)} />
      <StatCard title="Max Depth" value={formatDepth(maxDepthRecord, unitPreferences.depth)} />
      <StatCard title="Avg Depth" value={formatDepth(avgDepth, unitPreferences.depth)} trend={depthTrend?.trend} trendLabel={depthTrend?.label} />
      <StatCard title="Avg Temp" value={formatTemperature(avgTemp, unitPreferences.temperature)} trend={tempTrend?.trend} trendLabel={tempTrend?.label} />
      {ENABLE_GAS_ANALYTICS && (
        <StatCard
          title={`Avg ${getGasConsumptionMetricLabel(gasConsumptionMetric)}`}
          value={avgGasConsumption !== null ? formatGasConsumptionDisplayValue(avgGasConsumption, gasConsumptionMetric, unitPreferences.pressure) : '—'}
          subtitle={avgGasConsumption !== null ? `${divesWithGasConsumption.length} dive${divesWithGasConsumption.length !== 1 ? 's' : ''}` : 'No data'}
          trend={gasTrend?.trend}
          trendLabel={gasTrend?.label}
        />
      )}
    </>
  );

  return (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom>
        Dive Statistics
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        Based on {totalDives} dive{totalDives !== 1 ? 's' : ''}
        {validDives.length < totalDives ? ` (averages from ${validDives.length} with complete data)` : ''}
      </Typography>
      {isMobile ? (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 2, backgroundColor: 'divider' },
          }}
        >
          {cards}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {cards}
        </Box>
      )}
    </Box>
  );
};

export default DiveStatistics;